/**
 * PublishingProgress — modal overlay shown while a post is being published.
 *
 * Displays per-platform status with retry capability for failed platforms.
 * Lets the user start a new post or review results once all platforms are done.
 */

import { Loader2, CheckCircle2, XCircle, RefreshCw, RotateCcw, PlusCircle } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { Dialog, DialogContent } from '../../core/Dialog';
import { Button } from '../../core/Button';
import type { PerPlatformResult, PublishPhase } from '../../../types/composer';
import { PlatformBadge } from './PlatformBadge';

interface PublishingProgressProps {
  phase: PublishPhase;
  results: PerPlatformResult[];
  onRetry: (accountId: string) => void;
  onClose: () => void;
}

export function PublishingProgress({
  phase,
  results,
  onRetry,
  onClose,
}: Readonly<PublishingProgressProps>) {
  const { t } = useTranslation();
  const isOpen = phase !== 'idle';
  const isDone = phase === 'done';

  const successCount = results.filter((r) => r.status === 'success').length;
  const failCount = results.filter((r) => r.status === 'failed').length;
  const inFlight = results.some(
    (r) => r.status === 'uploading' || r.status === 'publishing' || r.status === 'queued'
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && isDone) onClose(); }}>
      <DialogContent hideClose={!isDone} className="max-w-md">
        {/* Header */}
        <div className="text-center pb-2">
          {!isDone && (
            <div className="flex items-center justify-center gap-2 mb-3">
              <Loader2 size={20} className="animate-spin text-brand-600" />
              <span className="font-semibold text-slate-800">{t('composer.publishing.inProgress')}</span>
            </div>
          )}
          {isDone && failCount === 0 && (
            <div className="flex flex-col items-center gap-2 mb-3">
              <CheckCircle2 size={32} className="text-emerald-500" />
              <span className="font-semibold text-slate-800">{t('composer.publishing.allSuccess')}</span>
              <span className="text-sm text-slate-500">
                {t('composer.publishing.publishedTo', { count: successCount })}
              </span>
            </div>
          )}
          {isDone && failCount > 0 && (
            <div className="flex flex-col items-center gap-2 mb-3">
              <div className="relative">
                <CheckCircle2 size={28} className="text-emerald-500" />
                <XCircle size={18} className="text-rose-500 absolute -bottom-1 -right-1 bg-white rounded-full" />
              </div>
              <span className="font-semibold text-slate-800">
                {t('composer.publishing.partialSuccess')}
              </span>
              <span className="text-sm text-slate-500">
                {successCount > 0 && t('composer.publishing.nSuccess', { count: successCount })}{' '}
                {failCount > 0 && t('composer.publishing.nFailed', { count: failCount })}
              </span>
            </div>
          )}
        </div>

        {/* Per-platform rows */}
        <div className="space-y-2">
          {results.map((result) => (
            <div
              key={result.accountId}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all',
                result.status === 'success' && 'bg-emerald-50 border-emerald-200',
                result.status === 'failed' && 'bg-rose-50 border-rose-200',
                (result.status === 'queued' || result.status === 'uploading' || result.status === 'publishing') && 'bg-slate-50 border-slate-200'
              )}
            >
              <PlatformBadge platform={result.platform} size="sm" />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{result.accountName}</p>
                <p className={cn('text-xs', STATUS_COLOR[result.status])}>
                  {t(`composer.publishing.status.${result.status}`)}
                  {result.error && ` — ${result.error}`}
                </p>
              </div>

              <StatusIcon status={result.status} />

              {result.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => onRetry(result.accountId)}
                  className="flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900 font-medium"
                  title={t('composer.publishing.retry')}
                >
                  <RotateCcw size={12} />
                  {t('composer.publishing.retry')}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        {isDone && (
          <div className="flex gap-2 pt-2">
            {failCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  results
                    .filter((r) => r.status === 'failed')
                    .forEach((r) => onRetry(r.accountId));
                }}
              >
                <RefreshCw size={13} />
                {t('composer.publishing.retryAll')}
              </Button>
            )}
            <Button
              size="sm"
              className="flex-1"
              onClick={onClose}
            >
              <PlusCircle size={13} />
              {t('composer.publishing.newPost')}
            </Button>
          </div>
        )}

        {/* In-flight disclaimer */}
        {inFlight && (
          <p className="text-[11px] text-slate-400 text-center pt-1">
            {t('composer.publishing.doNotClose')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: PerPlatformResult['status'] }) {
  switch (status) {
    case 'queued':
      return <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-dashed animate-spin" />;
    case 'uploading':
    case 'publishing':
      return <Loader2 size={16} className="text-brand-600 animate-spin shrink-0" />;
    case 'success':
      return <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />;
    case 'failed':
      return <XCircle size={16} className="text-rose-500 shrink-0" />;
  }
}

const STATUS_COLOR: Record<PerPlatformResult['status'], string> = {
  queued: 'text-slate-400',
  uploading: 'text-brand-600',
  publishing: 'text-brand-600',
  success: 'text-emerald-600',
  failed: 'text-rose-600',
};
